import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { normalizeReturnRedirectUrl, persistSession, restoreSession } from '@/services/auth';
import { useDispatch } from 'react-redux';
import { authActions } from '@/redux/auth/authSlice';

function resolveRedirectPath(redirect: string | null): string {
  if (!redirect) return '/calendar';
  try {
    const normalized = normalizeReturnRedirectUrl(redirect);
    const url = new URL(normalized);
    if (url.origin !== window.location.origin) return '/calendar';
    return `${url.pathname}${url.search}${url.hash}` || '/calendar';
  } catch {
    return '/calendar';
  }
}

const AuthPage = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [params] = useSearchParams();
  const redirect = params.get('redirect');
  const signedOut = params.get('signed_out') === '1';
  const [isAutoRestoring, setIsAutoRestoring] = useState(true);

  const accountsBase = useMemo(
    () => import.meta.env.VITE_ACCOUNTS_APP_URL?.replace(/\/+$/, '') || '',
    []
  );
  const audience = String(import.meta.env.VITE_AUTH_AUDIENCE || 'calendar-api').trim();

  const signInHref = useMemo(() => {
    if (!accountsBase) return '#';
    const u = new URL(`${accountsBase}/auth`);
    if (audience) u.searchParams.set('audience', audience);
    if (redirect) u.searchParams.set('redirect', normalizeReturnRedirectUrl(redirect));
    return u.toString();
  }, [accountsBase, audience, redirect]);

  useEffect(() => {
    if (signedOut) {
      setIsAutoRestoring(false);
      return;
    }

    let cancelled = false;
    const autoRestore = async () => {
      const restored = await restoreSession();
      if (cancelled) return;

      if (restored?.accessToken) {
        persistSession(restored);
        dispatch(authActions.hydrateFromSession());
        navigate(resolveRedirectPath(redirect), { replace: true });
        return;
      }

      if (accountsBase && signInHref !== '#') {
        window.location.replace(signInHref);
        return;
      }

      setIsAutoRestoring(false);
    };

    void autoRestore();
    return () => { cancelled = true; };
  }, [accountsBase, dispatch, navigate, redirect, signInHref, signedOut]);

  if (isAutoRestoring) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <div className="text-sm text-muted-foreground">Signing you in...</div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen flex-col items-center justify-center gap-6 bg-surface-muted px-4">
      <div className="max-w-md rounded-2xl border border-surface-muted bg-white p-8 text-center shadow-card dark:bg-surface-muted/40">
        <h1 className="text-xl font-semibold text-ink">
          {signedOut ? 'Signed out successfully' : 'Sign in required'}
        </h1>
        <p className="mt-2 text-sm text-ink-muted">
          {signedOut
            ? 'You are logged out from this app. Click below when you want to sign in again.'
            : 'Calendar uses the same session as Sculpx Accounts. Open the accounts app and sign in, then return here — or use the button below.'}
        </p>
        {accountsBase ? (
          <a
            href={signInHref}
            className="mt-6 inline-flex rounded-xl bg-brand px-5 py-2.5 text-sm font-semibold text-brand-foreground shadow-sm transition hover:opacity-95"
          >
            Go to Accounts sign in
          </a>
        ) : (
          <p className="mt-4 text-xs text-amber-700 dark:text-amber-200">
            Set <code className="rounded bg-black/5 px-1 dark:bg-white/10">VITE_ACCOUNTS_APP_URL</code>{' '}
            in <code className="rounded bg-black/5 px-1 dark:bg-white/10">.env</code>.
          </p>
        )}
        <p className="mt-6 text-sm">
          <Link to="/calendar" className="font-medium text-brand hover:underline">
            Try calendar again
          </Link>
        </p>
      </div>
    </div>
  );
};

export default AuthPage;
